/**
 * Life4Earth V2 — News API Adapter
 *
 * Frontend client for the news system. Talks to the L4E backend proxy
 * (which holds the NewsAPI.ai key server-side) and to the news CRUD endpoints.
 *
 * Endpoints:
 *   GET  /api/News/search                       → proxy to NewsAPI.ai
 *   GET  /api/News/sources/search               → search NewsAPI.ai source catalog
 *   GET  /api/News/sources                      → master source list
 *   POST /api/News/sources                      → add source to master list
 *   PUT  /api/News/sources/{sourceUri}           → update source (enable/disable)
 *   GET  /api/News/subscriptions/{scopeGuid}     → get subscription config
 *   PUT  /api/News/subscriptions/{scopeGuid}     → upsert subscription config
 *   GET  /api/News/feed/{scopeGuid}              → approved feed for a scope
 *   GET  /api/News/pending/{scopeGuid}           → pending moderation queue
 *   POST /api/News/moderate                      → approve/reject items
 *   GET  /api/News/community-access              → list community premium toggles
 *   PUT  /api/News/community-access/{guid}       → toggle premium access
 *   GET  /api/News/blocked-domains               → list blocked share domains
 *   POST /api/News/blocked-domains               → add a blocked domain
 *   DELETE /api/News/blocked-domains/{domain}     → remove a blocked domain
 */

import { apiFetch } from './client';

// ── Types ──────────────────────────────────────────────────────────

/** Article returned from the search proxy or feed. */
export interface NewsArticle {
  newsItemGuid: string;
  contentType: 'api_article' | 'user_blog' | 'community_share';
  provider: string;
  providerItemId?: string;
  canonicalUrl: string;
  title: string;
  excerpt?: string;
  sourceName?: string;
  sourceUri?: string;
  sourceDomain?: string;
  author?: string;
  imageUrl?: string;
  publishedAt?: string;
  language?: string;
  category?: string;
  tags?: string[];
  sentiment?: number;
  isDuplicate?: boolean;
  clusterId?: string;
  // Feed sharing fields
  chronicleGuid?: string;
  sharedBy?: string;
  shareComment?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  ogSiteName?: string;
  embedHtml?: string;
  embedType?: string;
}

/** Master source entry. */
export interface NewsSource {
  sourceUri: string;
  sourceName: string;
  sourceDomain?: string;
  provider: string;
  country?: string;
  language?: string;
  category?: string;
  isEnabled: boolean;
  isSeeded: boolean;
}

/** Community subscription config. */
export interface NewsSubscription {
  subscriptionGuid: string;
  scopeType: string;
  scopeGuid: string;
  keywords: string[];
  locationLabel?: string;
  locationUri?: string;
  topicCategories: string[];
  allowedSourceUris: string[] | null;
  blockedSourceUris: string[] | null;
  excludedCategories: string[] | null;
  languageFilter: string[];
  moderationMode: 'queue' | 'auto_approve';
  maxArticles: number;
  isEnabled: boolean;
}

/** Moderation record for the review queue. */
export interface NewsModerationItem {
  moderationGuid: string;
  newsItemGuid: string;
  scopeType: string;
  scopeGuid: string;
  status: 'pending' | 'approved' | 'rejected';
  contentType: string;
  reviewerUserGuid?: string;
  reviewedAt?: string;
  notes?: string;
  isFeatured: boolean;
  placement?: 'featured' | 'card' | 'headline';
  sortOrder?: number;
  article: NewsArticle;  // joined article data
  /**
   * DEV-987. When this article was inherited from an ancestor community,
   * the ancestor's entity_guid. NULL/undefined when the article belongs
   * to the requesting community itself. The Curate UI labels inherited
   * items with a "via {inheritedFromName}" badge.
   */
  inheritedFromGuid?: string | null;
  /** DEV-987. Display name of the ancestor; same NULL semantics as guid. */
  inheritedFromName?: string | null;
}

/** Community premium access toggle. */
export interface CommunityNewsAccess {
  communityGuid: string;
  communityName: string;
  newsEnabled: boolean;
}

// ── Search Proxy (calls NewsAPI.ai via backend) ────────────────────

export interface NewsSearchParams {
  scopeGuid: string;
  keywords?: string;
  location?: string;
  sourceUris?: string[];
  dateStart?: string;
  dateEnd?: string;
  language?: string;
  count?: number;
  page?: number;
}

export async function searchNews(params: NewsSearchParams): Promise<NewsArticle[]> {
  const qs = new URLSearchParams();
  qs.set('scopeGuid', params.scopeGuid);
  if (params.keywords) qs.set('keywords', params.keywords);
  if (params.location) qs.set('location', params.location);
  if (params.sourceUris?.length) qs.set('sourceUris', params.sourceUris.join(','));
  if (params.dateStart) qs.set('dateStart', params.dateStart);
  if (params.dateEnd) qs.set('dateEnd', params.dateEnd);
  if (params.language) qs.set('language', params.language);
  if (params.count) qs.set('count', String(params.count));
  if (params.page) qs.set('page', String(params.page));
  return apiFetch<NewsArticle[]>(`/api/News/search?${qs.toString()}`);
}

// ── Source Catalog Search ──────────────────────────────────────────

export interface SourceSearchResult {
  uri: string;
  title: string;
  domain?: string;
  country?: string;
  language?: string;
  category?: string;
}

export async function searchSourceCatalog(query: string): Promise<SourceSearchResult[]> {
  const qs = new URLSearchParams({ q: query });
  return apiFetch<SourceSearchResult[]>(`/api/News/sources/search?${qs.toString()}`);
}

// ── Master Source List ─────────────────────────────────────────────

export async function fetchMasterSources(): Promise<NewsSource[]> {
  return apiFetch<NewsSource[]>('/api/News/sources');
}

export async function addMasterSource(source: Partial<NewsSource>): Promise<NewsSource> {
  return apiFetch<NewsSource>('/api/News/sources', {
    method: 'POST',
    body: JSON.stringify(source),
  });
}

export async function updateMasterSource(
  sourceUri: string,
  update: { isEnabled?: boolean; category?: string }
): Promise<NewsSource> {
  return apiFetch<NewsSource>(`/api/News/sources/${encodeURIComponent(sourceUri)}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

// ── Community Premium Access ───────────────────────────────────────

export async function fetchCommunityNewsAccess(): Promise<CommunityNewsAccess[]> {
  return apiFetch<CommunityNewsAccess[]>('/api/News/community-access');
}

export async function toggleCommunityNewsAccess(
  communityGuid: string,
  enabled: boolean
): Promise<void> {
  await apiFetch<void>(`/api/News/community-access/${communityGuid}`, {
    method: 'PUT',
    body: JSON.stringify({ newsEnabled: enabled }),
  });
}

// ── Subscriptions ──────────────────────────────────────────────────

export async function fetchSubscription(scopeGuid: string): Promise<NewsSubscription | null> {
  try {
    return await apiFetch<NewsSubscription>(`/api/News/subscriptions/${scopeGuid}`);
  } catch (err: any) {
    if (err?.status === 404 || err?.message?.includes('404')) return null;
    throw err;
  }
}

export async function upsertSubscription(
  scopeGuid: string,
  config: Partial<NewsSubscription>
): Promise<NewsSubscription> {
  return apiFetch<NewsSubscription>(`/api/News/subscriptions/${scopeGuid}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// ── Feed (approved items) ──────────────────────────────────────────

export async function fetchNewsFeed(
  scopeGuid: string,
  opts?: { page?: number; count?: number; category?: string; contentType?: string }
): Promise<NewsModerationItem[]> {
  const qs = new URLSearchParams();
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.count) qs.set('count', String(opts.count));
  if (opts?.category) qs.set('category', opts.category);
  if (opts?.contentType) qs.set('contentType', opts.contentType);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<NewsModerationItem[]>(`/api/News/feed/${scopeGuid}${suffix}`);
}

// ── Hide / Unhide inherited items (DEV-987) ───────────────────────

/**
 * Sub-community admin opts out of an inherited article. Server persists
 * the hide in community_news_hidden_inherited. Idempotent.
 */
export async function hideInheritedArticle(
  scopeGuid: string,
  moderationGuid: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/News/feed/${scopeGuid}/hide-inherited/${moderationGuid}`,
    { method: 'POST' },
  );
}

/** Reverse hideInheritedArticle. Idempotent. */
export async function unhideInheritedArticle(
  scopeGuid: string,
  moderationGuid: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/News/feed/${scopeGuid}/hide-inherited/${moderationGuid}`,
    { method: 'DELETE' },
  );
}

// ── Moderation Queue ───────────────────────────────────────────────

export async function fetchPendingItems(scopeGuid: string): Promise<NewsModerationItem[]> {
  return apiFetch<NewsModerationItem[]>(`/api/News/pending/${scopeGuid}`);
}

/** Placement slot for approved articles in the feed layout. */
export type ArticlePlacement = 'featured' | 'card' | 'headline';

export interface ModerationAction {
  moderationGuid: string;
  action: 'approve' | 'reject';
  notes?: string;
  isFeatured?: boolean;
  placement?: ArticlePlacement;
}

/** Update placement for an already-approved article. */
export async function updateArticlePlacement(
  moderationGuid: string,
  placement: ArticlePlacement
): Promise<void> {
  await apiFetch<void>(`/api/News/moderate/${moderationGuid}/placement`, {
    method: 'PUT',
    body: JSON.stringify({ placement }),
  });
}

/** Batch-update sort_order for approved articles. */
export async function updateSortOrder(
  items: { moderationGuid: string; sortOrder: number }[]
): Promise<void> {
  await apiFetch<void>('/api/News/sort-order', {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

/** Fetch articles from NewsAPI.ai into the pending queue for a scope. */
export async function fetchArticlesIntoQueue(scopeGuid: string): Promise<{ fetched: number }> {
  return apiFetch<{ fetched: number }>(`/api/News/fetch/${scopeGuid}`, {
    method: 'POST',
  });
}

export async function moderateItems(actions: ModerationAction[]): Promise<void> {
  await apiFetch<void>('/api/News/moderate', {
    method: 'POST',
    body: JSON.stringify({ actions }),
  });
}

// ── Article Pool (cross-community sharing) ────────────────

/** Article from the shared pool, available for adoption. */
export interface PoolArticle {
  newsItemGuid: string;
  contentType: string;
  provider: string;
  canonicalUrl: string;
  title: string;
  excerpt?: string;
  sourceName?: string;
  sourceDomain?: string;
  imageUrl?: string;
  publishedAt?: string;
  category?: string;
  approvedByCount: number;
}

export interface PoolResponse {
  articles: PoolArticle[];
  total: number;
  page: number;
  count: number;
}

/** Browse the shared article pool, excluding items already linked to this scope. */
export async function fetchArticlePool(
  scopeGuid: string,
  opts?: { search?: string; page?: number; count?: number }
): Promise<PoolResponse> {
  const qs = new URLSearchParams();
  if (opts?.search) qs.set('search', opts.search);
  if (opts?.page) qs.set('page', String(opts.page));
  if (opts?.count) qs.set('count', String(opts.count));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<PoolResponse>(`/api/News/pool/${scopeGuid}${suffix}`);
}

/** Adopt articles from the shared pool into a scope's pending queue. */
export async function adoptPoolArticles(
  scopeGuid: string,
  newsItemGuids: string[]
): Promise<{ adopted: number }> {
  return apiFetch<{ adopted: number }>(`/api/News/pool/${scopeGuid}/adopt`, {
    method: 'POST',
    body: JSON.stringify({ newsItemGuids }),
  });
}

/**
 * Remove a single feed item from a community's feed.
 * Only removes from this scope — does not delete the underlying news item.
 */
export async function removeFeedItem(
  scopeGuid: string,
  moderationGuid: string
): Promise<void> {
  await apiFetch<void>(`/api/News/feed/${scopeGuid}/item/${moderationGuid}`, {
    method: 'DELETE',
  });
}

/**
 * Delete a post everywhere (author or system admin). Soft-deletes the
 * underlying news_item so it disappears from the main feed and every community
 * feed at once, while preserving its comments/reactions for audit/recovery.
 */
export async function deletePost(newsItemGuid: string): Promise<void> {
  await apiFetch<void>(`/api/News/post/${newsItemGuid}`, {
    method: 'DELETE',
  });
}

// ── Blocked Share Domains (platform-level blacklist) ──────────────

/** A domain blocked from community feed sharing. */
export interface BlockedShareDomain {
  id: string;
  domain: string;
  reason?: string;
  blockedBy?: string;
  createdAt: string;
  isActive: boolean;
  appealStatus: 'none' | 'pending' | 'upheld' | 'overturned';
  appealCount: number;
  lastAppealAt?: string;
  overturnedBy?: string;
  overturnedAt?: string;
  blockCount: number;
}

/** Fetch all blocked share domains. */
export async function fetchBlockedDomains(): Promise<BlockedShareDomain[]> {
  return apiFetch<BlockedShareDomain[]>('/api/News/blocked-domains');
}

/** Add a domain to the blocked list. */
export async function addBlockedDomain(
  domain: string,
  reason?: string
): Promise<BlockedShareDomain> {
  return apiFetch<BlockedShareDomain>('/api/News/blocked-domains', {
    method: 'POST',
    body: JSON.stringify({ domain, reason }),
  });
}

/** Remove a domain from the blocked list. */
export async function removeBlockedDomain(domain: string): Promise<void> {
  await apiFetch<void>(`/api/News/blocked-domains/${encodeURIComponent(domain)}`, {
    method: 'DELETE',
  });
}

// ── Blacklist Domain from Feed (admin enforcement) ────────────────

export interface BlacklistFromFeedRequest {
  moderationGuid: string;
  scopeGuid: string;
}

export interface BlacklistFromFeedResponse {
  domain: string;
  removed: boolean;
  notified: boolean;
  appealWorkflowGuid?: string;
}

/**
 * System admin action: extract the domain from a feed item, add it to
 * the blocked list, remove the post from the feed, and notify the sharer.
 * Creates a workflow request so the user can appeal.
 */
export async function blacklistDomainFromFeed(
  req: BlacklistFromFeedRequest
): Promise<BlacklistFromFeedResponse> {
  return apiFetch<BlacklistFromFeedResponse>('/api/News/blacklist-from-feed', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// ── Appeal Filing (user action) ───────────────────────────────────

/**
 * User files an appeal for a blocked domain.
 * Sets appeal_status to 'pending' and increments appeal_count.
 */
export async function fileBlockedDomainAppeal(
  domain: string,
  workflowGuid: string,
  reason: string
): Promise<BlockedShareDomain> {
  return apiFetch<BlockedShareDomain>(
    `/api/News/blocked-domains/${encodeURIComponent(domain)}/file-appeal`,
    { method: 'POST', body: JSON.stringify({ workflowGuid, reason }) }
  );
}

// ── Appeal Governance (admin resolution) ──────────────────────────

export interface ResolveAppealRequest {
  action: 'overturn' | 'uphold';
  workflowGuid?: string;
  notes?: string;
}

/**
 * System admin resolves an appeal for a blocked domain.
 * "overturn" clears the block and prevents community-level re-blocking.
 * "uphold" keeps the block in place and records the decision.
 */
export async function resolveBlockedDomainAppeal(
  domain: string,
  req: ResolveAppealRequest
): Promise<BlockedShareDomain> {
  return apiFetch<BlockedShareDomain>(
    `/api/News/blocked-domains/${encodeURIComponent(domain)}/resolve-appeal`,
    { method: 'POST', body: JSON.stringify(req) }
  );
}

/**
 * System admin force re-blocks a domain that was previously overturned.
 * Resets appeal_status to 'none' and increments block_count.
 */
export async function forceReblockDomain(
  domain: string,
  reason?: string
): Promise<BlockedShareDomain> {
  return apiFetch<BlockedShareDomain>(
    `/api/News/blocked-domains/${encodeURIComponent(domain)}/force-block`,
    { method: 'POST', body: JSON.stringify({ domain, reason }) }
  );
}
